import { Controller } from '@hotwired/stimulus'
import L from 'leaflet'
import { currentLocation, sendJSON } from '../lib/prayer_walk'

export default class extends Controller {
  static targets = ['map', 'status', 'form', 'name', 'category']
  static values = {
    points: Array,
    selected: Array,
    editable: Boolean,
    center: Object,
    tagUrl: String,
    untagUrl: String,
    createUrl: String,
    walkId: String
  }

  connect () {
    this.selected = new Set(this.selectedValue)
    this.map = L.map(this.mapTarget).setView([39.8283, -98.5795], 4)
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '© OpenStreetMap contributors'
    }).addTo(this.map)
    this.pointsValue.forEach(point => this.addPoint(point))
    if (this.centerValue.latitude != null) {
      this.map.setView([this.centerValue.latitude, this.centerValue.longitude], 14)
    } else if (this.pointsValue.length) {
      this.map.fitBounds(this.pointsValue.map(point => [point.latitude, point.longitude]), { padding: [30, 30], maxZoom: 15 })
    } else if (this.editableValue) {
      this.locate()
    }
    if (this.editableValue) this.map.on('click', event => this.dropPin(event.latlng))
    window.requestAnimationFrame(() => this.map.invalidateSize())
  }

  disconnect () {
    this.map?.remove()
    this.map = null
  }

  addPoint (point) {
    const marker = L.circleMarker([point.latitude, point.longitude], {
      ...this.markerStyle(point.id), bubblingMouseEvents: false
    }).addTo(this.map)
    marker.bindPopup(() => this.popup(point, marker), { className: 'prayer-point-popup' })
  }

  markerStyle (id) {
    return { radius: 10, color: this.selected.has(id) ? '#36852b' : '#3369ac', fillOpacity: 0.8 }
  }

  popup (point, marker) {
    const content = document.createElement('div')
    const heading = document.createElement('strong')
    heading.textContent = point.name
    content.append(heading)
    const description = document.createElement('p')
    description.textContent = `${point.category.replaceAll('_', ' ')} · ${point.peopleCount} people · 🙏 ${point.prayerCount}x`
    content.append(description)
    if (this.editableValue) {
      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = this.selected.has(point.id) ? 'Remove from this walk' : 'Tag on this walk'
      button.className = 'rounded border px-3 py-2'
      button.addEventListener('click', async () => {
        button.disabled = true
        const remove = this.selected.has(point.id)
        try {
          const updated = await sendJSON(remove ? this.untagUrlValue : this.tagUrlValue, remove ? 'DELETE' : 'POST', { prayer_point_id: point.id })
          Object.assign(point, updated)
          remove ? this.selected.delete(point.id) : this.selected.add(point.id)
          marker.setStyle(this.markerStyle(point.id))
          marker.closePopup()
          this.statusTarget.textContent = remove ? 'Point removed from this walk.' : 'Point tagged on this walk.'
        } catch (error) {
          this.statusTarget.textContent = error.message
        } finally {
          button.disabled = false
        }
      })
      content.append(button)
    }
    return content
  }

  dropPin (latlng) {
    if (this.newPin) this.map.removeLayer(this.newPin)
    this.newPin = L.marker(latlng, {
      draggable: true,
      icon: L.divIcon({ className: 'nature-map-pin', html: '<span class="nature-map-pin__marker"></span>', iconSize: [28, 28], iconAnchor: [14, 28] })
    }).addTo(this.map)
    this.formTarget.classList.remove('hidden')
    this.nameTarget.focus({ preventScroll: true })
  }

  cancel () {
    if (this.newPin) this.map.removeLayer(this.newPin)
    this.newPin = null
    this.formTarget.classList.add('hidden')
  }

  async create (event) {
    event.preventDefault()
    if (!this.newPin || this.creating) return
    this.creating = true
    const latlng = this.newPin.getLatLng()
    const point = {
      name: this.nameTarget.value.trim(),
      category: this.categoryTarget.value,
      latitude: latlng.lat,
      longitude: latlng.lng,
      prayerCount: 0,
      peopleCount: 0
    }
    try {
      const result = await sendJSON(this.createUrlValue, 'POST', { prayer_walk_id: this.walkIdValue, prayer_point: point })
      Object.assign(point, result)
      this.selected.add(point.id)
      this.addPoint(point)
      this.cancel()
      this.nameTarget.value = ''
      this.statusTarget.textContent = 'Shared prayer point created and tagged on this walk.'
    } catch (error) {
      this.statusTarget.textContent = error.message
    } finally {
      this.creating = false
    }
  }

  async locate () {
    const location = await currentLocation()
    if (this.map && location) this.map.setView([location.latitude, location.longitude], 14)
  }
}
